import {MaterialIcons} from '@expo/vector-icons';
import {StackScreenProps} from '@react-navigation/stack';
import {Box, Button, Divider, HStack, Icon, Text, VStack} from 'native-base';
import React, {useCallback} from 'react';
import {FlatList} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import SkeletonContent from 'react-native-skeleton-content';
import {t} from 'react-native-tailwindcss';
import {
  AccountStatus,
  TransactionFieldsFragment,
  useBalanceQuery,
  useOnBalanceUpdatedSubscription,
  useTransactionsQuery
} from '../api/generated';
import {LoadingEntriesSkeleton} from '../components/LoadingEntriesSkeleton';
import {PaymentItem} from '../components/PaymentItem';
import {useAsyncAction} from '../hooks/useAsyncAction';
import {useAccountContext} from '../lib/accountContext';
import {formatIntAmount} from '../lib/formatters';
import i18n from '../lib/i18n';
import {Nav, NavParams} from './navigation';

export const HomeScreen: React.FC<StackScreenProps<NavParams, Nav.HomeScreen>> = ({navigation}) => {
  const account = useAccountContext();
  const {data: balanceData, refetch: refetchBalance, loading: isBalanceLoading} = useBalanceQuery();
  const {
    data: trxData,
    refetch: refetchTrx,
    loading: isTrxLoading
  } = useTransactionsQuery({
    variables: {
      size: 20,
      from: 0
    }
  });

  const refetch = useCallback(async () => {
    return Promise.all([refetchTrx(), refetchBalance()]);
  }, []);
  const [onRefresh, isRefreshing] = useAsyncAction(refetch);
  const isLoading = isBalanceLoading || isTrxLoading;
  const payments = (trxData?.transactions.items || []).slice(0, 5);
  const balance = balanceData?.balance;
  const isVerified = !account || account.status === AccountStatus.VERIFIED;

  useOnBalanceUpdatedSubscription({
    onSubscriptionData: () => {
      void refetch();
    }
  });

  const handlePayout = () => {
    if (!account) return;
    if (account.status !== AccountStatus.VERIFIED) {
      return navigation.navigate(Nav.IdSessionScreen);
    }
    if (!account.fundsOrigin || !account.notPEP || !account.occupation) {
      return navigation.navigate(Nav.EditAccountScreen, {payout: true});
    }
    return navigation.navigate(Nav.PayoutScreen);
  };

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <Box style={[t.flex1, t.mT2]}>
        <HStack style={[t.pY4, t.pX5]} space={5} justifyContent="space-between" alignItems="center">
          <VStack space={1} flex={1}>
            <Text>
              <SkeletonContent
                isLoading={isLoading}
                containerStyle={[{width: 150, height: 40}, t.pT1]}
                layout={[{width: 150, height: 28}]}>
                <Text style={[t.text3xl, t.fontBold]}>{formatIntAmount(balance)}</Text>
              </SkeletonContent>
            </Text>
            <Text style={[t.textSm, t.textGray600]}>{i18n.t('currentBalance')}</Text>
          </VStack>
          <VStack alignItems="center" space={1}>
            <Button
              style={{width: 40, height: 40, borderRadius: 20}}
              onPress={() => navigation.navigate(Nav.RechargeScreen)}
              variant="outline"
              leftIcon={<Icon as={MaterialIcons} name="add" size="lg" />}></Button>
            <Text style={[t.textSm, t.textGray600]}>{i18n.t('addMoney')}</Text>
          </VStack>
          <VStack alignItems="center" space={1}>
            <Button
              style={{width: 40, height: 40, borderRadius: 20}}
              onPress={handlePayout}
              variant="outline"
              leftIcon={<Icon as={MaterialIcons} name="arrow-upward" size="lg" />}></Button>
            <Text style={[t.textSm, t.textGray600]}>{i18n.t('withdraw')}</Text>
          </VStack>
          <VStack alignItems="center" space={1}>
            <Button
              style={{width: 40, height: 40, borderRadius: 20}}
              onPress={() => navigation.navigate(Nav.ReceivePaymentScreen)}
              variant="outline"
              leftIcon={<Icon as={MaterialIcons} name="qr-code" size="lg" />}></Button>
            <Text style={[t.textSm, t.textGray600]}>{i18n.t('receive')}</Text>
          </VStack>
        </HStack>
        <Divider style={[t.mB2]} />
        <LoadingEntriesSkeleton isLoading={isLoading} numberOfEntries={6}>
          <FlatList<TransactionFieldsFragment>
            data={payments}
            onRefresh={onRefresh}
            refreshing={isRefreshing}
            ListFooterComponent={() => (
              <Button
                size="lg"
                variant="link"
                style={[t.mT4]}
                onPress={() => navigation.navigate(Nav.PaymentsScreen)}>
                {i18n.t('showAllPayments')}
              </Button>
            )}
            ListEmptyComponent={() => (
              <Text style={[t.textGray600, t.textCenter, t.pY6, t.pX4, t.flex1]}>
                {i18n.t('noPayments')}
              </Text>
            )}
            renderItem={({item}) => (
              <PaymentItem
                item={item}
                key={item.id}
                onPress={() => navigation.navigate(Nav.PaymentScreen, {paymentId: item.id})}
              />
            )}
            keyExtractor={(item) => item.id}
          />
          <HStack style={[t.p4]} space={3}>
            <Button flex={1} onPress={() => navigation.navigate(Nav.CreatePaymentScreens)}>
              {i18n.t('sendMoney')}
            </Button>
            <Button
              flex={1}
              onPress={() => navigation.navigate(Nav.ScanPaymentScreen)}
              leftIcon={<Icon as={MaterialIcons} name="qr-code-scanner" size="sm" />}>
              {i18n.t('scanToPay')}
            </Button>
          </HStack>
        </LoadingEntriesSkeleton>
      </Box>
    </SafeAreaView>
  );
};
